/* Tiny fetch client for this page's own /api/view + /api/sign — no auth token, no refresh (this
   page has no session; the single-use token in each request body IS the authorization). Mirrors
   the error-shape handling in platform/apps/web/src/lib/api.ts for consistency. */

export interface ViewResponse {
  title: string;
  content: string | null;
  fileUrl?: string | null;
  email: string;
  name?: string | null;
  // present when the issuer required identity verification for this signer
  kycRequired?: boolean;
  kycStatus?: KycStatus | null;
  kycNote?: string | null;
}

export type KycStatus = 'NOT_STARTED' | 'PENDING' | 'VERIFIED' | 'REJECTED' | 'NOT_REQUIRED';

export interface KycStatusResponse {
  status: KycStatus;
  note?: string | null;
}

export interface SignResponse {
  ok: true;
  documentHash: string;
  signatureHash: string;
  trustedTimestamp: { kind: string; authority?: string; time: string };
  seal: { alg: string; keyId: string };
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "Can't reach the signing service right now. Check your connection and try again.");
  }

  const text = await res.text();
  let data: any = null;
  if (text) {
    try { data = JSON.parse(text); } catch { /* handled by the !res.ok branch below */ }
  }
  if (!res.ok) {
    const msg = data?.message
      ? (Array.isArray(data.message) ? data.message.join(', ') : data.message)
      : `Something went wrong (${res.status}).`;
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const viewContract = (token: string): Promise<ViewResponse> => post('/api/view', { token });

export const signContract = (token: string, typedName: string, consentText: string): Promise<SignResponse> =>
  post('/api/sign', { token, typedName, consentText });

export const getKycStatus = (token: string): Promise<KycStatusResponse> =>
  post('/api/kyc-status', { token });

/** Submit identity documents for review (multipart). The browser sets the multipart Content-Type +
 *  boundary automatically for a FormData body — do NOT set it by hand. The token travels as a form
 *  field (same authorization model as the JSON calls). Returns the resulting status (PENDING). */
export async function uploadKyc(token: string, form: FormData): Promise<KycStatusResponse> {
  form.set('token', token);
  let res: Response;
  try {
    res = await fetch('/api/kyc-upload', { method: 'POST', body: form });
  } catch {
    throw new ApiError(0, "Can't reach the signing service right now. Check your connection and try again.");
  }
  const text = await res.text();
  let data: any = null;
  if (text) { try { data = JSON.parse(text); } catch { /* handled below */ } }
  if (!res.ok) {
    const msg = data?.message
      ? (Array.isArray(data.message) ? data.message.join(', ') : data.message)
      : `Something went wrong (${res.status}).`;
    throw new ApiError(res.status, msg);
  }
  return data as KycStatusResponse;
}

const MIME_EXT: Record<string, string> = {
  'application/pdf': '.pdf', 'image/png': '.png', 'image/jpeg': '.jpg', 'image/gif': '.gif',
  'image/webp': '.webp', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};

/** Fetch the contract's attached file as bytes (a Blob, not JSON) via the signing service's byte
 *  proxy (POST /api/file). The token in the body is the only authorization, same as the other calls.
 *  Returns a best-effort download filename from Content-Disposition, falling back to a MIME-derived
 *  extension so the saved file is openable. */
export async function fetchContractFile(token: string): Promise<{ blob: Blob; filename: string }> {
  let res: Response;
  try {
    res = await fetch('/api/file', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch {
    throw new ApiError(0, "Can't reach the signing service right now. Check your connection and try again.");
  }
  if (!res.ok) {
    let message = 'The document could not be loaded.';
    try { const j = (await res.json()) as { message?: string }; if (j?.message) message = j.message; } catch { /* non-JSON */ }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const m = /filename="?([^";]+)"?/.exec(cd);
  const filename = m?.[1] || `document${MIME_EXT[blob.type] || ''}`;
  return { blob, filename };
}
